import * as React from 'react';

import PropTypes from 'prop-types';
import { compose } from 'recompose';
import type { LegacyContextType } from 'types/legacy-context-types';

import connectToRouter from 'js/lib/connectToRouter';
import connectToStores from 'js/lib/connectToStores';
import Retracked from 'js/lib/retracked';

import DefaultHeader from 'bundles/composable-page-header-consumer/header-renderers/DefaultHeader';
import UniversalPageFeatures from 'bundles/composable-page-header/components/UniversalPageFeatures';
import PageFooter from 'bundles/page-footer/components/PageFooter';
import { getPromotionSeoProperties } from 'bundles/promotions/utils/promotionSeoProperties';
import SimpleCourseraMetatags from 'bundles/seo/components/SimpleCourseraMetatags';
import ApplicationStoreClass from 'bundles/ssr/stores/ApplicationStore';

import 'css!../styl/index';

type PropsFromCaller = {
  children?: JSX.Element;
};

type PropsFromRouter = {
  promoCode?: string;
};

type PropsFromStores = {
  requestCountryCode?: string;
  csrfToken?: string;
};

type PropsToComponent = PropsFromCaller & PropsFromRouter & PropsFromStores;

export class PromotionsApp extends React.Component<PropsToComponent> {
  static contextTypes = {
    router: PropTypes.object.isRequired,
    getStore: PropTypes.func.isRequired,
  };

  declare context: LegacyContextType<typeof PromotionsApp.contextTypes>;

  static childContextTypes = {
    csrfToken: PropTypes.string,
    requestCountryCode: PropTypes.string,
  };

  getChildContext() {
    return {
      csrfToken: this.props.csrfToken,
      requestCountryCode: this.props.requestCountryCode,
    };
  }

  render() {
    const { promoCode } = this.props;
    const seoProperties = promoCode && getPromotionSeoProperties(promoCode);

    const metatags =
      promoCode && seoProperties
        ? {
            description: seoProperties.description,
            disableCourseraDescriptionSuffix: true,
            disableCrawlerIndexing: false,
            disableDescLimit: true,
            title: seoProperties.title,
            ogAndTwitterTitle: seoProperties.title,
          }
        : { disableCrawlerIndexing: true, disableCourseraSuffix: true };

    return (
      <div className="rc-PromotionsApp">
        <DefaultHeader showMetaNav />
        <UniversalPageFeatures />
        <SimpleCourseraMetatags {...metatags} />
        <div className="c-PromotionsChildrenWrapper container">{this.props.children}</div>
        <PageFooter />
      </div>
    );
  }
}

export default compose<PropsToComponent, PropsFromCaller>(
  connectToRouter<PropsFromRouter, PropsFromCaller>((router) => {
    return {
      promoCode: router.params.promoCode,
    };
  }),
  Retracked.createTrackedContainer(() => {
    return {
      namespace: {
        app: 'promotions',
        page: 'promotions_page',
      },
    };
  }),
  connectToStores<PropsFromStores, PropsFromRouter>([ApplicationStoreClass], (ApplicationStore, props) => ({
    ...props,
    csrfToken: ApplicationStore.getState().csrfToken,
    requestCountryCode: ApplicationStore.getState().requestCountryCode,
  }))
)(PromotionsApp);
